import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);
const USER_CACHE_KEY = "vgws_user_cache_v1";
const REFRESH_MS = 5 * 60 * 1000;

function readCache() {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeCache(user) {
  try { sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user)); }
  catch {}
}
function clearCache() {
  try { sessionStorage.removeItem(USER_CACHE_KEY); } catch {}
}

export function AuthProvider({ children }) {
  const cached = readCache();
  const [user, setUser] = useState(cached);
  const [loading, setLoading] = useState(!cached);  // no blocking splash if we already have cache
  const [config, setConfig] = useState({ auth_mock: false, google_auth: true, process_owner_sub_statuses: [] });
  const lastFetched = useRef(0);
  const inFlight = useRef(null);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const now = Date.now();
    if (!force && cached && now - lastFetched.current < REFRESH_MS && lastFetched.current > 0) {
      return;
    }
    if (inFlight.current) return inFlight.current;
    inFlight.current = (async () => {
      try {
        const { data } = await api.get("/auth/me");
        // Invalidate cache if the identity changed under us
        if (cached && cached.id && data?.id && cached.id !== data.id) {
          clearCache();
        }
        setUser(data); writeCache(data);
        lastFetched.current = Date.now();
      } catch {
        setUser(null); clearCache();
      } finally {
        setLoading(false);
        inFlight.current = null;
      }
    })();
    return inFlight.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    // Revalidate on window focus but only if stale
    const onFocus = () => {
      if (Date.now() - lastFetched.current > REFRESH_MS) refresh({ force: true });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const exchangeSession = async (session_id) => {
    const { data } = await api.post("/auth/session", {}, {
      headers: { "X-Session-ID": session_id },
    });
    setUser(data); writeCache(data); lastFetched.current = Date.now();
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearCache();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{
      user, loading, config, refresh, exchangeSession, logout, formatApiError,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
